import { useState } from 'react';
import { CenterContainer } from './CenterContainer';
import { BaseButton, Text } from 'components';
import { Trans, t } from '@lingui/macro';
import styled from '@emotion/styled';
import { EnvelopeSimple, Buildings, User, Lock, EyeClosed, Eye } from '@phosphor-icons/react';
import { AuthLink } from './AuthLink';
import { AuthButton } from './AuthButton';
import { InputField, Form, useForm } from 'features/form';
import type { Fields } from 'features/form';
import * as Yup from 'yup';
import { useTheme } from '@emotion/react';
import { useSupplierSignUp } from '../api/useSupplierSignUp';
import { useEvent } from 'lib/segment';
import { useUser } from 'features/auth';

const validationsStep1 = Yup.object().shape({
  email: Yup.string()
    .trim()
    .required(t`Email is required`)
    .email(t`Invalid email address.`)
    .test('has-tld', t`Invalid email address.`, value => {
      if (!value) return true; // empty is allowed
      return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,63}$/.test(value);
    }),
});

const companyNameValidation = Yup.string()
  .required(t`Please enter a company name`)
  .max(255, t`Company name is too long`);

const validationsNewUser = Yup.object().shape({
  companyName: companyNameValidation,
  yourName: Yup.string()
    .required(t`Please enter your name`)
    .max(255, t`Your name is too long`),
  password: Yup.string()
    .required(t`Please create a password`)
    .min(6, t`Password must be at least 6 characters long`)
    .max(255, t`Password must be less than 255 characters long`),
  repeatPassword: Yup.string()
    .required(t`Please repeat your password`)
    .oneOf([Yup.ref('password')], t`Passwords must match`),
});

const validationsExistingUser = Yup.object().shape({
  companyName: companyNameValidation,
  yourName: Yup.string()
    .required(t`Please enter your name`)
    .max(255, t`Your name is too long`),
});

const formFieldsStep1: Fields = {
  email: {
    type: 'text',
    id: 'email',
    required: true,
    autoComplete: 'username',
    icon: <EnvelopeSimple />,
    placeholder: t`Email`,
  },
};

const formFieldsNewUser: Fields = {
  companyName: {
    type: 'text',
    id: 'companyName',
    required: true,
    placeholder: t`Enter company name`,
    icon: <Buildings />,
    autoComplete: 'organization',
  },
  yourName: {
    type: 'text',
    id: 'yourName',
    required: true,
    placeholder: t`Enter your name`,
    icon: <User />,
    autoComplete: 'name',
  },
  password: {
    type: 'text',
    id: 'password',
    required: true,
    placeholder: t`Enter password`,
    icon: <Lock />,
    autoComplete: 'new-password',
  },
  repeatPassword: {
    type: 'text',
    id: 'repeatPassword',
    required: true,
    placeholder: t`Repeat password`,
    icon: <Lock />,
    autoComplete: 'off',
  },
};

const formFieldsExistingUser: Fields = {
  companyName: {
    type: 'text',
    id: 'companyName',
    required: true,
    placeholder: t`Enter company name`,
    icon: <Buildings />,
    autoComplete: 'organization',
  },
  yourName: {
    type: 'text',
    id: 'yourName',
    required: true,
    placeholder: t`Enter your name`,
    icon: <User />,
    autoComplete: 'name',
  },
};

export const SupplierSignUp = () => {
  const { signupResponse } = useSupplierSignUp();

  return (
    <CenterContainer
      size={signupResponse ? 'large' : 'small'}
      welcomeText={
        signupResponse ? (
          <Trans>
            PLM designed for Fashion & Lifestyle developed by actual buyers and designers
          </Trans>
        ) : (
          <Trans>... Welcome back</Trans>
        )
      }
    >
      {!signupResponse ? <SignUpStep1 /> : null}
      {signupResponse?.isNewUser ? <SignUpNewUser /> : null}
      {signupResponse &&
      !signupResponse.isNewUser &&
      signupResponse.userAssociatedSupplierAccounts?.length ? (
        <SignUpExistingUserWithPortal />
      ) : null}
      {signupResponse &&
      !signupResponse.isNewUser &&
      !signupResponse.userAssociatedSupplierAccounts?.length ? (
        <SignUpExistingUserWithoutPortal />
      ) : null}
    </CenterContainer>
  );
};

export const SignUpNewUser = () => {
  const { getSignupResponse, signUp2ndStep, isPending2ndStep, clearSignupResponse } =
    useSupplierSignUp();
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const theme = useTheme();
  const event = useEvent();
  const { data: userData } = useUser();

  const onSubmit = async () => {
    const result = await submit();
    if (result.errors.length === 0) {
      const signupResponse = getSignupResponse();
      event('Supplier Organization Signup Initiated', {
        objectType: 'Form',
        action: 'Create',
        targetType: 'Supplier Organization',
        targetName: String(result.data.companyName),
        targetId: userData?.loginContext?.inviteCode,
        businessContext: {
          email: signupResponse?.email,
          organizationName: String(result.data.companyName),
        },
        source: 'Button',
        page: 'Supplier Signup',
      });

      signUp2ndStep({
        data: {
          loginId: signupResponse?.id || '',
          userName: String(result.data.yourName),
          password: String(result.data.password),
          confirmPassword: String(result.data.repeatPassword),
          companyName: String(result.data.companyName),
        },
      });
    }
  };
  const { fields, submit, disableBtn } = useForm({
    fields: formFieldsNewUser,
    validations: validationsNewUser,
  });
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        onSubmit();
      }}
    >
      <Form validations={validationsNewUser} width="100%">
        <FormContainer gap={0}>
          <Text variant="headlineMd" weight="semiBold" bottom={8}>
            <Trans>Welcome</Trans>
          </Text>
          <Text variant="bodyMd" bottom={24} color={theme.palette.text.secondary}>
            <Trans>Please fill out your details</Trans>
          </Text>
          <InputField field={fields.yourName} w={100} />
          <InputField field={fields.companyName} w={100} />
          <InputField
            field={{
              ...fields.password,
              inputType: showPassword ? 'text' : 'password',
              endIcon: (
                <BaseButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  title={showPassword ? t`Click to hide password` : t`Click to reveal password`}
                >
                  {showPassword ? <Eye /> : <EyeClosed />}
                </BaseButton>
              ),
            }}
            w={100}
          />
          <InputField
            field={{
              ...fields.repeatPassword,
              inputType: showRepeatPassword ? 'text' : 'password',
              endIcon: (
                <BaseButton
                  type="button"
                  onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                  tabIndex={-1}
                  title={
                    showRepeatPassword ? t`Click to hide password` : t`Click to reveal password`
                  }
                >
                  {showRepeatPassword ? <Eye /> : <EyeClosed />}
                </BaseButton>
              ),
            }}
            w={100}
          />
        </FormContainer>
      </Form>
      <div>
        <AuthButton
          disabled={disableBtn}
          margin={24}
          isLoading={isPending2ndStep}
          buttonText={t`Sign up`}
        />
        <AuthLink
          linkTo="/supplier/signup"
          questionText={t`Not what you're looking for?`}
          linkText={t`Click here to cancel`}
          onClick={() => clearSignupResponse()}
          light
        />
      </div>
    </form>
  );
};

export const SignUpStep1 = () => {
  const { signUp, isPending, getSignupResponse } = useSupplierSignUp();
  const event = useEvent();
  const { data: userData } = useUser();

  const onSubmit = async () => {
    const result = await submit();
    if (result.errors.length === 0) {
      signUp({ data: { email: String(result.data.email) } });
      const signupResponse = getSignupResponse();
      event('Supplier Email Verification OTP Sent', {
        objectType: 'Email',
        action: 'Create',
        targetType: 'OTP',
        targetId: userData?.loginContext?.inviteCode,
        targetName: 'OTP Verification',
        businessContext: {
          email: signupResponse?.email,
          inviteCode: userData?.loginContext?.inviteCode,
        },
        source: 'System',
        page: 'Supplier Signup',
      });
    }
  };
  const { fields, submit } = useForm({
    fields: formFieldsStep1,
    validations: validationsStep1,
  });
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        onSubmit();
      }}
    >
      <Form validations={validationsStep1} width="100%">
        <FormContainer gap={16}>
          <Text variant="headlineXs" weight="semiBold">
            <Trans>Sign up</Trans>
          </Text>
          <Text variant="bodySm" bottom={16}>
            <Trans>
              Create an account to access powerful tools for managing your supply chain,
              collaborating with partners, and ensuring compliance—all in one place.
            </Trans>
          </Text>
          <InputField field={fields.email} w={100} />
        </FormContainer>
      </Form>
      <div>
        <AuthButton isLoading={isPending} hasAuthLink buttonText={t`Sign up`} />
        <AuthLink linkTo="/login" questionText={t`Already a user?`} linkText={t`Log in here`} />
      </div>
    </form>
  );
};

export const SignUpExistingUserWithPortal = () => {
  const { signupResponse, signUp2ndStep, isPending2ndStep, companySignIn, clearSignupResponse } =
    useSupplierSignUp();
  const theme = useTheme();

  const onSubmit = async () => {
    const result = await submit();
    if (result.errors.length === 0) {
      signUp2ndStep({
        data: {
          loginId: signupResponse?.id || '',
          companyName: String(result.data.companyName),
          userName: String(result.data.yourName),
        },
      });
    }
  };
  const { fields, submit, disableBtn } = useForm({
    fields: formFieldsExistingUser,
    validations: validationsExistingUser,
  });
  const onSelectCompany = (id: number, name: string) => {
    companySignIn(id, name);
  };
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        onSubmit();
      }}
    >
      <Form validations={validationsExistingUser} width="100%">
        <FormContainer gap={8}>
          <Text variant="headlineMd" weight="semiBold" noWrap>
            <Trans>Welcome</Trans> {signupResponse?.email}
          </Text>
          <Text variant="bodyMd" bottom={16} color={theme.palette.text.secondary}>
            <Trans>
              You already have a Supplier Portal account, do you wish to log in or create another
              one?
            </Trans>
          </Text>
          {signupResponse?.userAssociatedSupplierAccounts?.length ? (
            <LoginExisting>
              {signupResponse?.userAssociatedSupplierAccounts?.map(account => (
                <div key={account.supplierId}>
                  <BaseButton
                    type="button"
                    onClick={() => onSelectCompany(account.supplierId, account.supplierName)}
                  >
                    <Text variant="bodySm" color={theme.palette.text.link} weight="semiBold">
                      Log in to {account.supplierName}
                    </Text>
                  </BaseButton>
                </div>
              ))}
            </LoginExisting>
          ) : null}
          <InputField field={fields.yourName} w={100} />
          <InputField
            field={{
              ...fields.companyName,
              helperText: t`Fill out the name of your company to create another account.`,
            }}
            w={100}
          />
        </FormContainer>
      </Form>
      <div>
        <AuthButton
          disabled={disableBtn}
          isLoading={isPending2ndStep}
          hasAuthLink
          buttonText={t`Sign up`}
        />
        <AuthLink
          linkTo="/supplier/signup"
          questionText={t`Not what you were looking for?`}
          linkText={t`Click here to cancel`}
          onClick={() => clearSignupResponse()}
          light
        />
      </div>
    </form>
  );
};

export const SignUpExistingUserWithoutPortal = () => {
  const {
    getSignupResponse,
    signUp2ndStep,
    isPending2ndStep,
    clearSignupResponse,
    signupResponse,
  } = useSupplierSignUp();
  const theme = useTheme();

  const onSubmit = async () => {
    const result = await submit();
    if (result.errors.length === 0) {
      const signUpres = getSignupResponse();

      signUp2ndStep({
        data: {
          loginId: signUpres?.id || '',
          companyName: String(result.data.companyName),
          userName: String(result.data.yourName),
        },
      });
    }
  };
  const { fields, submit, disableBtn } = useForm({
    fields: formFieldsExistingUser,
    validations: validationsExistingUser,
  });
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        onSubmit();
      }}
    >
      <Form validations={validationsExistingUser} width="100%">
        <FormContainer gap={4}>
          <Text variant="headlineMd" weight="semiBold">
            <Trans>Welcome</Trans> {signupResponse?.email}
          </Text>
          <Text variant="bodyMd" bottom={24} color={theme.palette.text.secondary}>
            <Trans>
              Log in with your Delogue account credentials and enter your company name to create
              your Supplier Portal Account.
            </Trans>
          </Text>
          <InputField field={fields.yourName} w={100} />
          <InputField field={fields.companyName} w={100} />
        </FormContainer>
      </Form>
      <div>
        <AuthButton
          disabled={disableBtn}
          isLoading={isPending2ndStep}
          hasAuthLink
          buttonText={t`Sign up`}
        />
        <AuthLink
          linkTo="/supplier/signup"
          questionText={t`Not what you were looking for?`}
          linkText={t`Click here to cancel`}
          onClick={() => clearSignupResponse()}
          light
        />
      </div>
    </form>
  );
};

const FormContainer = styled.div<{ gap: number }>`
  margin: 24px;
  margin-bottom: 16px;
  gap: ${p => p.gap}px;
  display: flex;
  flex-direction: column;
  width: calc(100% - 48px);
  svg {
    fill: ${p => p.theme.palette.fg.primary};
  }
  .MuiFormHelperText-root {
    font-size: ${p => p.theme.typography.bodyXs.fontSize};
    color: ${p => p.theme.palette.text.tertiary};
    line-height: 16px;
  }
`;

const LoginExisting = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 16px;
  button {
    max-width: calc(100% - 32px);
    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      max-width: 100%;
    }
    :hover {
      span {
        text-decoration: underline;
      }
    }
    :focus {
      span {
        outline: 1px solid ${p => p.theme.palette.border.brand};
      }
    }
  }
`;
